import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Chip,
} from '@mui/material';
import {
  Lock as LockIcon,
  AdminPanelSettings as AdminIcon,
  Work as TeamSupervisorIcon,
  Home as HouseholdIcon,
  Email as EmailIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { householdService } from '../services/householdService';
import { userService } from '../services/userService';
import { roleService } from '../services/roleService';
import authService from '../services/authService';

// TabPanel component defined outside to prevent re-creation
const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: '20px' }}>
    {value === index && children}
  </div>
);

const Login = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Crew login state
  const [crewEmail, setCrewEmail] = useState('');
  const [crewAuthCode, setCrewAuthCode] = useState('');
  const [crewCodeSent, setCrewCodeSent] = useState(false);

  // Household login state
  const [householdEmail, setHouseholdEmail] = useState('');
  const [householdAuthCode, setHouseholdAuthCode] = useState('');
  const [householdCodeSent, setHouseholdCodeSent] = useState(false);
  
  // Check if user is already authenticated and redirect
  useEffect(() => {
    const checkAuth = () => {
      if (authService.isAuthenticated()) {
        const user = authService.getUser();
        if (user) {
          console.log('User already authenticated, redirecting...', user);
          // Check user roles to determine redirect
          const hasAdminRole = user.roleIds && user.roleIds.some(roleId => 
            ['1', '2', '3'].includes(roleId.toString()) // Admin, Team Supervisor, or Crew roles
          );
          
          if (hasAdminRole) {
            console.log('Redirecting to crew dashboard');
            navigate('/crew/dashboard');
          } else {
            console.log('Redirecting to household dashboard');
            navigate('/household/dashboard');
          }
        }
      }
    };
    
    // Only check once when component mounts
    checkAuth();
  }, []); // Empty dependency array - only run once


  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    // Reset state when switching tabs
    if (newValue === 0) {
      setCrewCodeSent(false);
      setCrewAuthCode('');
      setCrewEmail('');
    } else {
      setHouseholdCodeSent(false);
      setHouseholdAuthCode('');
      setHouseholdEmail('');
    }
  };

  const handleCrewRequestCode = async (e) => {
    e.preventDefault();
    
    if (!crewEmail.trim()) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await authService.requestAuthCode(crewEmail, 'crew');
      
      if (response.success) {
        setCrewCodeSent(true);
        setError('');
      } else {
        setError(response.message || 'Failed to send authentication code');
      }

    } catch (err) {
      console.error('Crew code request error:', err);
      setError('Failed to send authentication code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCrewVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!crewAuthCode.trim()) {
      setError('Please enter the authentication code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await authService.verifyAuthCode(crewEmail, crewAuthCode, 'crew');
      
      if (response.success) {
        console.log('Crew authentication successful, navigating to crew dashboard');
        // JWT tokens are automatically stored by authService
        // Navigate to crew dashboard
        navigate('/crew/dashboard');
      } else {
        setError(response.message || 'Invalid authentication code');
      }

    } catch (err) {
      console.error('Crew code verification error:', err);
      setError('Authentication failed. Please check your code and try again.');
    } finally {
      setLoading(false);
    }
  };



  const handleHouseholdRequestCode = async (e) => {
    e.preventDefault();
    
    if (!householdEmail.trim()) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await authService.requestAuthCode(householdEmail, 'household');
      
      if (response.success) {
        setHouseholdCodeSent(true);
        setError('');
      } else {
        setError(response.message || 'Failed to send authentication code');
      }

    } catch (err) {
      console.error('Household code request error:', err);
      setError('Failed to send authentication code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleHouseholdVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!householdAuthCode.trim()) {
      setError('Please enter the authentication code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await authService.verifyAuthCode(householdEmail, householdAuthCode, 'household');
      
      if (response.success) {
        console.log('Household authentication successful, navigating to household dashboard');
        // JWT tokens are automatically stored by authService
        // Navigate to household dashboard
        navigate('/household/dashboard');
      } else {
        setError(response.message || 'Invalid authentication code');
      }

    } catch (err) {
      console.error('Household code verification error:', err);
      setError('Authentication failed. Please check your code and try again.');
    } finally {
      setLoading(false);
    }
  };



  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Login
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Choose your login method based on your role
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} centered>
              <Tab 
                icon={<AdminIcon />} 
                label="TS/Crew/Staff" 
                iconPosition="start"
              />
              <Tab 
                icon={<HouseholdIcon />} 
                label="Household" 
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Crew Login */}
          <TabPanel value={activeTab} index={0}>
            {!crewCodeSent ? (
              <Box component="form" onSubmit={handleCrewRequestCode}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email Address"
                  value={crewEmail}
                  onChange={(e) => setCrewEmail(e.target.value)}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading || !crewEmail.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <EmailIcon />}
                >
                  {loading ? 'Sending Code...' : 'Send Login Code'}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  We'll send a one-time code to your email
                </Typography>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleCrewVerifyCode}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter the 6-digit code sent to {crewEmail}
                </Typography>
                <TextField
                  fullWidth
                  label="Authentication Code"
                  value={crewAuthCode}
                  onChange={(e) => setCrewAuthCode(e.target.value)}
                  sx={{ mb: 3 }}
                  autoFocus
                  placeholder="Enter 6-digit code"
                  inputProps={{ maxLength: 6 }}
                  InputProps={{
                    startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading || !crewAuthCode.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <AdminIcon />}
                >
                  {loading ? 'Verifying...' : 'Access Dashboard'}
                </Button>
                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setCrewCodeSent(false);
                    setCrewAuthCode('');
                    setError('');
                  }}
                  sx={{ mt: 1 }}
                >
                  Use Different Email
                </Button>
              </Box>
            )}
          </TabPanel>

          {/* Household Login */}
          <TabPanel value={activeTab} index={1}>
            {!householdCodeSent ? (
              <Box component="form" onSubmit={handleHouseholdRequestCode}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email Address"
                  value={householdEmail}
                  onChange={(e) => setHouseholdEmail(e.target.value)}
                  sx={{ mb: 3 }}
                  autoFocus
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading || !householdEmail.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <EmailIcon />}
                >
                  {loading ? 'Sending Code...' : 'Send Login Code'}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  We'll send a one-time code to your email
                </Typography>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleHouseholdVerifyCode}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter the 6-digit code sent to {householdEmail}
                </Typography>
                <TextField
                  fullWidth
                  label="Authentication Code"
                  value={householdAuthCode}
                  onChange={(e) => setHouseholdAuthCode(e.target.value)}
                  sx={{ mb: 3 }}
                  autoFocus
                  placeholder="Enter 6-digit code"
                  inputProps={{ maxLength: 6 }}
                  InputProps={{
                    startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading || !householdAuthCode.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <HouseholdIcon />}
                >
                  {loading ? 'Verifying...' : 'Access Household Dashboard'}
                </Button>
                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setHouseholdCodeSent(false);
                    setHouseholdAuthCode('');
                    setHouseholdEmail('');
                    setError('');
                  }}
                  sx={{ mt: 1 }}
                >
                  Use Different Email
                </Button>
              </Box>
            )}
          </TabPanel>

          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Don't have an account?
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              sx={{ mt: 1 }}
            >
              Register Here
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Login; 